/**
 * Ashrafie React Query Hooks
 *
 * TanStack Query v5 hooks for the Ashrafie rewards ledger. Mutations
 * automatically invalidate the account snapshot + history cache so balances
 * refresh after a redemption lands.
 *
 * @module lib/api/ashrafie/hooks
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ashrafieApi, AshrafieApiError } from './client';
import { ashrafieKeys } from './queryKeys';
import type {
  AshrafieAccountSnapshot,
  AshrafieBadgeAward,
  AshrafieCursorPage,
  AshrafiePassport,
  AshrafiePublicReputation,
  AshrafiePublicReputationApi,
  AshrafieRedemption,
  AshrafieRedemptionStatus,
  AshrafieReferralSummary,
  AshrafieReputation,
  AshrafieReputationVisibility,
  AshrafieReputationVisibilityResult,
  AshrafieStampSet,
  AshrafieStampSetApi,
  AshrafieTransaction,
  AshrafieTrophy,
  AshrafieTrophyApi,
  AshrafieTrophyCatalogApi,
  AshrafieTrophyCatalogItem,
  AshrafieTrophyClaimAddress,
  AttributeReferralRequest,
  ClaimHandleRequest,
  CreateRedemptionRequest,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Account
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the current user's Ashrafie balance + lifetime stats.
 * Returns undefined when the user is unauthenticated (hook disabled).
 */
export function useAshrafieAccount(
  options?: Omit<
    UseQueryOptions<AshrafieAccountSnapshot, AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: ashrafieKeys.account.snapshot(),
    queryFn: () =>
      ashrafieApi.get<AshrafieAccountSnapshot>('/account', {
        requiresAuth: true,
      }),
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Infinite-scroll hook over GET /account/history.
 * Pages are cursor-paginated with 20 items by default.
 */
export function useAshrafieHistory(
  limit: number = 20,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: ashrafieKeys.account.history(limit),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);
      const page = await ashrafieApi.getPaginated<AshrafieTransaction>(
        `/account/history?${params.toString()}`,
        { requiresAuth: true }
      );
      return page satisfies AshrafieCursorPage<AshrafieTransaction>;
    },
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    enabled: (options?.enabled ?? true) && ashrafieApi.isAuthenticated(),
    staleTime: 15 * 1000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Passport
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the composed Ashrafie Passport read model (balance, tier + progress,
 * badges, breadth stats and recent activity) from GET /passport. Served by the
 * backend's O(1) denormalized projection, so this is a single cheap read that
 * powers the whole Passport page. Disabled while unauthenticated so anonymous
 * visitors never trigger a 401.
 */
export function useAshrafiePassport(
  options?: Omit<
    UseQueryOptions<AshrafiePassport, AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafiePassport, AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.passport.all(),
    queryFn: () =>
      ashrafieApi.get<AshrafiePassport>('/passport', { requiresAuth: true }),
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 30 * 1000,
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Badges
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the current user's earned Ashrafie badges (achievement milestones),
 * newest first, from GET /account/badges. Disabled while the user is
 * unauthenticated so anonymous visitors never trigger a 401.
 */
export function useAshrafieBadges(
  options?: Omit<
    UseQueryOptions<AshrafieBadgeAward[], AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafieBadgeAward[], AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.badges.all(),
    queryFn: () =>
      ashrafieApi.get<AshrafieBadgeAward[]>('/account/badges', {
        requiresAuth: true,
      }),
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Redemptions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Redeem Ashrafies into a charity campaign donation. Required headers:
 * X-Idempotency-Key is generated per mutation call by the caller -- do not
 * reuse the same key across retries unless the server returned a 5xx.
 */
export function useAshrafieRedeem(
  options?: UseMutationOptions<
    AshrafieRedemption,
    AshrafieApiError,
    CreateRedemptionRequest & { idempotencyKey: string }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ idempotencyKey, ...body }) =>
      ashrafieApi.post<AshrafieRedemption>('/redemptions', body, {
        requiresAuth: true,
        headers: { 'X-Idempotency-Key': idempotencyKey },
      }),
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({ queryKey: ashrafieKeys.account.all() });
      queryClient.invalidateQueries({
        queryKey: ashrafieKeys.redemptions.all(),
      });
      (options?.onSuccess as ((...args: unknown[]) => unknown) | undefined)?.(
        data,
        variables,
        ...rest
      );
    },
  });
}

/** Paginated list of the user's past redemptions. */
export function useAshrafieRedemptions(
  filters?: { limit?: number; status?: AshrafieRedemptionStatus }
) {
  const limit = filters?.limit ?? 20;
  return useInfiniteQuery({
    queryKey: ashrafieKeys.redemptions.list({
      limit,
      ...(filters?.status && { status: filters.status }),
    }),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);
      if (filters?.status) params.set('status', filters.status);
      return ashrafieApi.getPaginated<AshrafieRedemption>(
        `/redemptions?${params.toString()}`,
        { requiresAuth: true }
      );
    },
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 30 * 1000,
  });
}

/** Single redemption detail with embedded campaign metadata. */
export function useAshrafieRedemption(
  redemptionId: string,
  options?: Omit<
    UseQueryOptions<AshrafieRedemption, AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: ashrafieKeys.redemptions.detail(redemptionId),
    queryFn: () =>
      ashrafieApi.get<AshrafieRedemption>(`/redemptions/${redemptionId}`, {
        requiresAuth: true,
      }),
    enabled: Boolean(redemptionId) && ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Referrals
// ─────────────────────────────────────────────────────────────────────────────

/** The current user's referral code + funnel counters (GET /account/referral). */
export function useAshrafieReferral(
  options?: Omit<
    UseQueryOptions<AshrafieReferralSummary, AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafieReferralSummary, AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.referral.summary(),
    queryFn: () =>
      ashrafieApi.get<AshrafieReferralSummary>('/account/referral', {
        requiresAuth: true,
      }),
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Attach a referral code to the caller (POST /referrals/attribute). Intended to be
 * fired once, post-registration, with a captured `?ref=` code. Invalidates the
 * referral summary so the inviter's counters can refresh on next view.
 */
export function useAttributeReferral(): UseMutationResult<
  void,
  AshrafieApiError,
  AttributeReferralRequest
> {
  const queryClient = useQueryClient();
  return useMutation<void, AshrafieApiError, AttributeReferralRequest>({
    mutationFn: (body) =>
      ashrafieApi.post<void>('/referrals/attribute', body, {
        requiresAuth: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ashrafieKeys.referral.all() });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Reputation & public profile
// ─────────────────────────────────────────────────────────────────────────────

/** The owner's full reputation model incl. visibility controls (GET /account/reputation). */
export function useAshrafieReputation(
  options?: Omit<
    UseQueryOptions<AshrafieReputation, AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafieReputation, AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.reputation.mine(),
    queryFn: () =>
      ashrafieApi.get<AshrafieReputation>('/account/reputation', {
        requiresAuth: true,
      }),
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * A public profile by handle (GET /reputation/:handle) — NO auth required. A 404
 * is an expected "not found / private" state, so it is not retried. Enabled only
 * when a handle is present.
 */
export function usePublicReputation(
  handle: string | undefined,
  options?: Omit<
    UseQueryOptions<AshrafiePublicReputation, AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafiePublicReputation, AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.reputation.public(handle ?? ''),
    queryFn: async () => {
      const raw = await ashrafieApi.get<AshrafiePublicReputationApi>(
        `/reputation/${encodeURIComponent(handle ?? '')}`,
        { requiresAuth: false }
      );
      // Normalize the backend DTO to the FE shape: `handle` → `publicHandle`,
      // `tier.{currentSlug,currentName}` → `tier.{slug,name}`, and unwrap badges
      // from either a bare array or a `{ earned }` envelope. Fixes the "@undefined"
      // handle, the blank tier medal, and the missing badge strip.
      return {
        ...raw,
        publicHandle: raw.handle ?? raw.publicHandle,
        tier: raw.tier
          ? {
              slug: raw.tier.currentSlug ?? null,
              name: raw.tier.currentName ?? null,
            }
          : undefined,
        badges: Array.isArray(raw.badges)
          ? raw.badges
          : (raw.badges?.earned ?? []),
      };
    },
    enabled: Boolean(handle),
    staleTime: 60 * 1000,
    retry: (failureCount, error) =>
      error.statusCode !== 404 && failureCount < 2,
    ...options,
  });
}

/**
 * Update reputation visibility toggles (PATCH /account/reputation/visibility).
 * Optimistically flips the cached toggles for an instant, snappy settings UX,
 * rolls back on failure, and re-syncs from the server on settle.
 */
export function useUpdateReputationVisibility(): UseMutationResult<
  AshrafieReputationVisibilityResult,
  AshrafieApiError,
  Partial<AshrafieReputationVisibility & { isPublic: boolean }>,
  { previous?: AshrafieReputation }
> {
  const queryClient = useQueryClient();
  return useMutation<
    AshrafieReputationVisibilityResult,
    AshrafieApiError,
    Partial<AshrafieReputationVisibility & { isPublic: boolean }>,
    { previous?: AshrafieReputation }
  >({
    mutationFn: (patch) =>
      ashrafieApi.patch<AshrafieReputationVisibilityResult>(
        '/account/reputation/visibility',
        patch,
        { requiresAuth: true }
      ),
    onMutate: async (patch) => {
      const key = ashrafieKeys.reputation.mine();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AshrafieReputation>(key);
      if (previous) {
        const { isPublic, ...visibilityPatch } = patch;
        queryClient.setQueryData<AshrafieReputation>(key, {
          ...previous,
          ...(isPublic !== undefined ? { isPublic } : {}),
          visibility: { ...previous.visibility, ...visibilityPatch },
        });
      }
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ashrafieKeys.reputation.mine(),
          context.previous
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ashrafieKeys.reputation.all(),
      });
    },
  });
}

/** Claim a public handle (POST /account/reputation/handle). */
export function useClaimHandle(): UseMutationResult<
  AshrafieReputationVisibilityResult,
  AshrafieApiError,
  ClaimHandleRequest
> {
  const queryClient = useQueryClient();
  return useMutation<
    AshrafieReputationVisibilityResult,
    AshrafieApiError,
    ClaimHandleRequest
  >({
    mutationFn: (body) =>
      ashrafieApi.post<AshrafieReputationVisibilityResult>(
        '/account/reputation/handle',
        body,
        { requiresAuth: true }
      ),
    onSuccess: (data) => {
      // The endpoint returns a FLAT visibility state, NOT a full reputation
      // record. Merging the flat fields back into the cached reputation and
      // re-nesting the visibility flags keeps `reputation.mine()` well-formed —
      // writing `data` directly would drop `visibility` and crash every reader
      // of `visibility.*`.
      queryClient.setQueryData<AshrafieReputation>(
        ashrafieKeys.reputation.mine(),
        (prev) =>
          prev
            ? {
                ...prev,
                isPublic: data.isPublic,
                publicHandle: data.publicHandle,
                visibility: {
                  showTier: data.showTier,
                  showBadges: data.showBadges,
                  showParticipation: data.showParticipation,
                  showTenure: data.showTenure,
                },
              }
            : prev
      );
      queryClient.invalidateQueries({
        queryKey: ashrafieKeys.reputation.all(),
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Trophies
// ─────────────────────────────────────────────────────────────────────────────

/** Normalize an array-or-{items} envelope body into a plain array. */
function toArray<T>(value: T[] | { items?: T[]; [k: string]: unknown }): T[] {
  if (Array.isArray(value)) return value;
  const nested = (value as { items?: T[]; trophies?: T[]; sets?: T[] }) ?? {};
  return nested.items ?? nested.trophies ?? nested.sets ?? [];
}

/** The current user's earned trophies (GET /account/trophies). */
export function useAshrafieTrophies(
  options?: Omit<
    UseQueryOptions<AshrafieTrophy[], AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafieTrophy[], AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.trophies.mine(),
    queryFn: async () => {
      const res = await ashrafieApi.get<AshrafieTrophyApi[]>(
        '/account/trophies',
        { requiresAuth: true }
      );
      // Normalize to the FE shape: `unlockId` → `id` (also fixes the broken
      // claim POST to /account/trophies/undefined/claim), `unlockedAt` →
      // `earnedAt`, `kind` → `isPhysical`, and flatten the nested claim status.
      return toArray(res).map((t) => ({
        id: t.unlockId,
        name: t.name,
        description: t.description,
        imageUrl: t.imageUrl,
        serialNumber: t.serialNumber,
        earnedAt: t.unlockedAt,
        isPhysical: t.kind === 'PHYSICAL',
        claimStatus: t.claim?.status ?? null,
      }));
    },
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}

/** The trophy catalog with the user's per-trophy state (GET /trophies/catalog). */
export function useAshrafieTrophyCatalog(
  options?: Omit<
    UseQueryOptions<AshrafieTrophyCatalogItem[], AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafieTrophyCatalogItem[], AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.trophies.catalog(),
    queryFn: async () => {
      const res = await ashrafieApi.get<AshrafieTrophyCatalogApi[]>(
        '/trophies/catalog',
        { requiresAuth: true }
      );
      // Normalize to the FE shape: `slug` → `id`, `kind` → `isPhysical`, and
      // derive `remaining` from the edition counters (null when unlimited).
      return toArray(res).map((e) => ({
        id: e.slug,
        name: e.name,
        description: e.description,
        imageUrl: e.imageUrl,
        state: e.state,
        isPhysical: e.kind === 'PHYSICAL',
        remaining:
          e.editionLimit == null ? null : e.editionLimit - e.editionIssued,
      }));
    },
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}

/** Claim a physical trophy edition (POST /account/trophies/:id/claim). */
export function useClaimTrophy(): UseMutationResult<
  AshrafieTrophy,
  AshrafieApiError,
  { trophyId: string; address: AshrafieTrophyClaimAddress }
> {
  const queryClient = useQueryClient();
  return useMutation<
    AshrafieTrophy,
    AshrafieApiError,
    { trophyId: string; address: AshrafieTrophyClaimAddress }
  >({
    mutationFn: ({ trophyId, address }) =>
      ashrafieApi.post<AshrafieTrophy>(
        `/account/trophies/${encodeURIComponent(trophyId)}/claim`,
        address,
        { requiresAuth: true }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ashrafieKeys.trophies.all() });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Stamps
// ─────────────────────────────────────────────────────────────────────────────

/** The user's stamp book — themed sets with per-stamp state (GET /account/stamps). */
export function useAshrafieStamps(
  options?: Omit<
    UseQueryOptions<AshrafieStampSet[], AshrafieApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<AshrafieStampSet[], AshrafieApiError> {
  return useQuery({
    queryKey: ashrafieKeys.stamps.mine(),
    queryFn: async () => {
      const res = await ashrafieApi.get<AshrafieStampSetApi[]>(
        '/account/stamps',
        { requiresAuth: true }
      );
      // Normalize to the FE shape: set `slug` → `id`, `earnedCount`/`totalCount`
      // → `earned`/`total`, and each stamp's `slug` → `id`. This is what fixes
      // the live stamp book rendering NaN/NaN with no visible stamps.
      return toArray(res).map((s) => ({
        id: s.slug,
        name: s.name,
        description: s.description,
        season: s.season,
        earned: s.earnedCount,
        total: s.totalCount,
        stamps: (s.stamps ?? []).map((st) => ({ ...st, id: st.slug })),
      }));
    },
    enabled: ashrafieApi.isAuthenticated(),
    staleTime: 60 * 1000,
    ...options,
  });
}
